import { FC, useEffect, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  useTheme,
} from "@mui/material";
import { observer } from "mobx-react";

import { CheckIcon } from "../../../icons/CheckIcon";
import { DeleteIcon } from "../../../icons/DeleteIcon";
import ChevronDownIcon from "../../../icons/ChevronDownIcon";
import { ViewesIcon } from "../../../icons/ViewesIcon";

import { useClaimsStore } from "../../../../hooks/useClaimsStore";
import type { AllFilters, ClaimFilterView, SavedClaimViewFilters } from "../../../../types/claims";
import { ClaimFilterViewDeleteDialog } from "./ClaimFilterViewDeleteDialog";

interface ClaimsFilterViewsDropdownProps {
  bordered?: boolean;
}

const ClaimsFilterViewsDropdown: FC<ClaimsFilterViewsDropdownProps> = observer(({ bordered = true }) => {
  const claimsStore = useClaimsStore();
  const theme = useTheme();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ClaimFilterView | null>(
    null,
  );

  const open = Boolean(anchorEl);

  useEffect(() => {
    claimsStore.loadClaimFilterViews();
  }, []);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const applyView = (viewId: string, filters: SavedClaimViewFilters) => {
    const { orderBy, columnVisibility, ...restFilters } = filters;

    const keys = Object.keys(restFilters);
    claimsStore.setSelectedFilterView(viewId);
    claimsStore.setAllFilters(keys.length > 0 ? { ...(restFilters as AllFilters) } : null);
    if (orderBy) {
      claimsStore.setOrderBy(orderBy);
    }
    if (columnVisibility && typeof columnVisibility === "object") {
      claimsStore.applyColumnVisibilityFromSavedView(columnVisibility);
    }
    handleClose();
  };

  const openDeleteConfirm = (view: ClaimFilterView) => {
    handleClose();
    setDeleteTarget(view);
  };

  if (
    claimsStore.isClaimFilterViewsLoading ||
    !claimsStore.claimFilterViews ||
    claimsStore.claimFilterViews.length === 0
  ) {
    return null;
  }

  return (
    <>
      <Button
        variant="text"
        onClick={handleOpen}
        aria-label="Filter views"
        aria-controls={open ? "claims-filter-views-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        startIcon={
          <Box
            component="span"
            aria-hidden
            sx={{
              display: "inline-flex",
              "& svg": { width: 18, height: 18 },
            }}
          >
            <ViewesIcon />
          </Box>
        }
        endIcon={<ChevronDownIcon />}
        sx={{
          textTransform: "none",
          fontWeight: 400,
          minWidth: "auto",
          width: "110px",
          whiteSpace: "nowrap",
          backgroundColor: theme.palette.background.paper,
          ...(bordered && {
            border: "1px solid",
            borderColor: theme.palette.divider,
            borderRadius: "8px",
            height: "36px",
            px: 1.5,
          }),
          "&:hover": {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        Views
      </Button>

      <Menu
        id="claims-filter-views-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              minWidth: 300,
              maxWidth: 420,
              boxShadow: theme.shadows[8],
              border: `1px solid ${theme.palette.divider}`,
            },
          },
          list: { dense: true },
        }}
      >
        {claimsStore.claimFilterViews.map((view) => {
          const isSelected = claimsStore.selectedFilterView === view.id;

          return (
            <MenuItem
              key={view.id}
              selected={isSelected}
              onClick={() => applyView(view.id, view.filters)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                py: 0.75,
                pr: 0.5,
              }}
            >
              <ListItemIcon sx={{ minWidth: "25px !important" }}>
                {isSelected ? (
                  <CheckIcon size={18} />
                ) : (
                  <Box sx={{ width: 18 }} aria-hidden />
                )}
              </ListItemIcon>
              <ListItemText
                primary={view.name}
                primaryTypographyProps={{
                  noWrap: true,
                  title: view.name,
                }}
                sx={{ flex: "1 1 auto", minWidth: 0, mr: 0.5 }}
              />

              <Box
                sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <Tooltip title="Delete saved view">
                  <IconButton
                    size="small"
                    aria-label={`Delete saved view ${view.name}`}
                    onClick={() => openDeleteConfirm(view)}
                    sx={{
                      color: "text.secondary",
                      "&:hover": { color: "error.main" },
                    }}
                  >
                    <DeleteIcon size={15} />
                  </IconButton>
                </Tooltip>
              </Box>
            </MenuItem>
          );
        })}
      </Menu>

      <ClaimFilterViewDeleteDialog
        open={!!deleteTarget}
        view={deleteTarget}
        onClose={() => setDeleteTarget(null)}
      />
    </>
  );
});

export default ClaimsFilterViewsDropdown;
